import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import debounce from 'lodash.debounce'

import projectDataSlice from 'store/features/projectData/slice'
import projectSlice from 'store/features/project/slice'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectThunks } from 'store/features/project/thunks'
import { calculateFrameIndex } from 'utils/thumbnail'
import { TRootState, TAppDispatch } from 'store/types'
import { IMediaItem, MediaStatus } from 'types/media'
import { IProjectThumbnailPath } from 'store/types/project'
import { projectSelectors } from 'store/features/project/selectors'

type TListenerApi = Parameters<
  Parameters<typeof thumbnailListenerMiddleware.startListening>[0]['effect']
>[1]

const projectDataActions = projectDataSlice.actions
const projectActions = projectSlice.actions
const thumbnailListenerMiddleware = createListenerMiddleware<
  TRootState,
  TAppDispatch
>()

/**
 * Compares previous and current media items to check if the frame would be different
 * Returns true if the frame changed (different item ID, file path, or frame index for videos)
 * Note: We compare by path and frameIndex, not by frameUrl, because frameUrl (blob URL) is recreated each time
 */
const hasFrameChanged = (
  previousThumbnailPath: IProjectThumbnailPath | null,
  currentMediaItem: IMediaItem | null
): boolean => {
  if (!previousThumbnailPath && !currentMediaItem) return false
  if (!previousThumbnailPath || !currentMediaItem) return true

  const currentPath =
    currentMediaItem.meta.previewPath || currentMediaItem.meta.path || ''
  const currentFrameIndex = calculateFrameIndex(currentMediaItem)

  return (
    previousThumbnailPath.id !== currentMediaItem.id ||
    previousThumbnailPath.path !== currentPath ||
    previousThumbnailPath.frameIndex !== currentFrameIndex
  )
}

/**
 * Debounced thumbnail generation
 * Uses 300ms debounce to avoid repeated work on rapid changes
 * Gets the current state at execution time to ensure we use the latest firstVisualMediaItems
 * Only generates thumbnail if the frame actually changed
 */
const debouncedGenerateThumbnail = debounce(
  async (listenerApi: TListenerApi) => {
    const state = listenerApi.getState()
    const projectId = projectSelectors.id(state)

    if (!projectId) {
      return
    }

    const firstVisualMediaItems =
      projectDataSelectors.firstVisualMediaItems(state)
    const projectThumbnailPath = state.project.projectThumbnailPath

    if (!firstVisualMediaItems) {
      // Delete thumbnail when all media items are removed
      if (projectThumbnailPath) {
        await listenerApi.dispatch(projectThunks.deleteProjectThumbnail())
      }
      return
    }

    //Only proceed if status is OK (file is ready for thumbnail extraction)
    if (firstVisualMediaItems?.meta.status !== MediaStatus.OK) {
      return
    }

    const isFrameChanged = hasFrameChanged(
      projectThumbnailPath,
      firstVisualMediaItems
    )

    if (!isFrameChanged) {
      return
    }

    await listenerApi.dispatch(
      projectThunks.generateAndUploadProjectThumbnail(firstVisualMediaItems)
    )
  },
  300
)

thumbnailListenerMiddleware.startListening({
  matcher: isAnyOf(
    projectActions.setProject,
    projectDataActions.setMediaItems,
    projectDataActions.updateTimelineItems,
    projectDataActions.undo,
    projectDataActions.redo,
    projectDataActions.addMediaItems,
    projectDataActions.uploadFromMediaLibrary,
    projectDataActions.updateMediaItem,
    projectDataActions.updateMediaItemWithoutUndoSave,
    projectDataActions.updateUploadedMediaItems,
    projectDataActions.removeVisualMediaItem,
    projectDataActions.removeTimelineSelectedItems
  ),
  effect: async (_, listenerApi) => {
    debouncedGenerateThumbnail(listenerApi)
  }
})

export const thumbnailMiddleware = thumbnailListenerMiddleware.middleware
